import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { EventService } from 'src/app/core/service/event.service';
import { MenuSearchGroup, MenuSearchResult, MenuSearchService } from '../services/menu-search.service';
import { LEFT_SIDEBAR_TYPE_CONDENSED, LEFT_SIDEBAR_TYPE_DEFAULT } from '../config/layout.model';
import { BrandItem } from '../models/brands.model';
import { Language } from '../models/language.model';
import { NotificationItem } from '../models/notification.model';
import { ProfileOptionItem } from '../models/profileoption.model';
import {AuthService} from "../../../modules/shared/services/auth.service";
import {LocalStorageService} from "../../../modules/shared/services/local-storage.service";
import {ModalService} from "../../../modules/shared/services/modal.service";
import {ChangePasswordModalComponent} from "./change-password-modal/change-password-modal.component";
import {TranslateService} from "@ngx-translate/core";

@Component({
  selector: 'app-topbar',
  templateUrl: './topbar.component.html',
  styleUrls: ['./topbar.component.scss'],
  providers: [ModalService]
})
export class TopbarComponent implements OnInit {

  @Input() layoutType: string = '';
  @Input() leftSidebarTheme: string = 'light';
  notificationList: NotificationItem[] = [];
  languages: Language[] = [];
  brands: BrandItem[] = [];
  profileOptions: ProfileOptionItem[] = [];
  selectedLanguage?: Language;
  loggedInUser: any = {};
  topnavCollapsed: boolean = false;
  currentLang: string = 'pl';
  availableLangs: string[] = ['pl', 'en'];
  langFlags: { [key: string]: string } = {
    pl: 'assets/images/flags/poland.svg',
    en: 'assets/images/flags/united-kingdom.svg',
  };

  // output events
  @Output() mobileMenuButtonClicked = new EventEmitter();

  // global menu search ("command palette")
  @ViewChild('menuSearchInput') menuSearchInput?: ElementRef<HTMLInputElement>;
  searchQuery: string = '';
  searchGroups: MenuSearchGroup[] = [];
  /** Flat view of all matched results, used for keyboard navigation. */
  searchFlat: MenuSearchResult[] = [];
  /** Index of the keyboard-highlighted result within searchFlat (-1 = none). */
  searchActiveIndex: number = -1;
  searchOpen: boolean = false;
  private searchBlurTimer: any;

  constructor (
    private authService: AuthService,
    private eventService: EventService,
    private localStorage: LocalStorageService,
    private modalService: ModalService,
    private translateService: TranslateService,
    private menuSearchService: MenuSearchService,
    private router: Router
  ) {

  }

  ngOnInit(): void {
    this.setMenuCollapsingState();
    this._fetchNotifications();
    this._fetchBrands();

    this.loggedInUser = this.authService.getUser();
    this.currentLang = this.localStorage.getItem('ui_lang') || 'pl';
    this.translateService.use(this.currentLang);
  }

  switchLang(lang: string): void {
    if (lang === this.currentLang) {
      return;
    }
    this.currentLang = lang;
    this.translateService.use(lang);
    this.localStorage.setItem('ui_lang', lang);
  }

  setMenuCollapsingState() {
    const collapsed = this.localStorage.getItem('collapsed');

    if (collapsed) {
      this.eventService.broadcast('changeLeftSidebarType', LEFT_SIDEBAR_TYPE_CONDENSED);
    }
  }

  /**
   * Fetches notifications
   */
  _fetchNotifications(): void {
    this.notificationList = [
      {
        text: 'Dodatki do produktów',
        subText: 'Od teraz zarządzanie dodatkami do produktów przenieśliśmy bezpośrednio o szczegółów produktów. Będziesz miał wszystko pod ręką!',
        icon: 'mdi mdi-comment-account-outline',
        bgColor: 'danger',
        redirectTo: '/apps/restaurant/menu/details'
      },
    ];
  }

  /**
   * Fetches brands
   */
  _fetchBrands(): void {
    this.brands = [{
      id: 1,
      name: 'Slack',
      logo: 'assets/images/brands/slack.png',
    },
    {
      id: 2,
      name: 'Github',
      logo: 'assets/images/brands/github.png',
    },
    {
      id: 3,
      name: 'Dribbble',
      logo: 'assets/images/brands/dribbble.png',
    },
    {
      id: 4,
      name: 'Bitbucket',
      logo: 'assets/images/brands/bitbucket.png',
    },
    {
      id: 5,
      name: 'Dropbox',
      logo: 'assets/images/brands/dropbox.png',
    },
    {
      id: 6,
      name: 'G Suite',
      logo: 'assets/images/brands/g-suite.png',
    }];
  }

  /**
   * changes left sidebar width 
   */
  changeSidebarWidth(): void {
    if (document.body.hasAttribute('data-sidebar-size') && document.body.getAttribute('data-sidebar-size') === "condensed") {
      this.eventService.broadcast('changeLeftSidebarType', LEFT_SIDEBAR_TYPE_DEFAULT);
      this.localStorage.setItem('collapsed', false);
    } else {
      this.eventService.broadcast('changeLeftSidebarType', LEFT_SIDEBAR_TYPE_CONDENSED);
      this.localStorage.setItem('collapsed', true);
    }
  }


  /**
 * Toggle the menu bar when having mobile screen
 */
  toggleMobileMenu(event: any) {
    this.topnavCollapsed = !this.topnavCollapsed;
    event.preventDefault();
    this.mobileMenuButtonClicked.emit();
  }

  /** Recompute results as the user types. */
  onSearchInput(): void {
    this.searchGroups = this.menuSearchService.search(this.searchQuery);
    this.searchFlat = this.flattenGroups(this.searchGroups);
    this.searchActiveIndex = this.searchFlat.length ? 0 : -1;
    this.searchOpen = this.searchQuery.trim().length > 0;
  }

  /** Keyboard navigation within the results dropdown. */
  onSearchKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.clearSearch();
      this.menuSearchInput?.nativeElement.blur();
      return;
    }

    if (!this.searchOpen) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (this.searchFlat.length) {
        this.searchActiveIndex = (this.searchActiveIndex + 1) % this.searchFlat.length;
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (this.searchFlat.length) {
        this.searchActiveIndex =
          (this.searchActiveIndex - 1 + this.searchFlat.length) % this.searchFlat.length;
      }
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const result = this.searchFlat[this.searchActiveIndex];
      if (result) {
        this.selectResult(result);
      }
    }
  }

  /** Navigate to a result and reset the search. */
  selectResult(result: MenuSearchResult): void {
    this.clearSearch();
    this.router.navigateByUrl(result.link);
  }

  /** Global index of a result (for aria-activedescendant + active styling). */
  resultIndex(result: MenuSearchResult): number {
    return this.searchFlat.indexOf(result);
  }

  /** Delay closing on blur so a result click registers first. */
  onSearchBlur(): void {
    this.searchBlurTimer = setTimeout(() => (this.searchOpen = false), 150);
  }

  onSearchFocus(): void {
    if (this.searchBlurTimer) {
      clearTimeout(this.searchBlurTimer);
    }
    if (this.searchQuery.trim().length > 0) {
      this.searchOpen = true;
    }
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.searchGroups = [];
    this.searchFlat = [];
    this.searchActiveIndex = -1;
    this.searchOpen = false;
  }

  trackByLink(_index: number, result: MenuSearchResult): string {
    return result.link;
  }

  /** Ctrl/Cmd+K focuses the search input from anywhere in the panel. */
  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && (event.key === 'k' || event.key === 'K')) {
      event.preventDefault();
      this.menuSearchInput?.nativeElement.focus();
    }
  }

  private flattenGroups(groups: MenuSearchGroup[]): MenuSearchResult[] {
    const flat: MenuSearchResult[] = [];
    groups.forEach(group => group.results.forEach(result => flat.push(result)));
    return flat;
  }

  logout() {
    this.authService.logout(true);
  }

    changePassword() {
      this.modalService.openCustomModal(ChangePasswordModalComponent, {}).result.then((result) => {}).catch(() => {});
    }
}
